/**
 * Pericope study message — whole-passage variant of the scripture-first
 * message. Shows the In Context section only (no interactive verse, no
 * analysis accordion) plus the commentary flow. Print and Report live in
 * the panel's pinned toolbar, not here.
 */

import { useState } from 'react';
import type { ScriptureResponse } from '@skjv/common';
import type { ProgressStep, InferenceProgress, InferenceMeta } from '@state/chat';
import { PericopeSection } from './pericope-section';
import { CommentaryButton } from './commentary-button';
import { CommentarySection, deriveAnchorContext } from './commentary-section';
import { ThoughtsAccordion } from './thoughts-accordion';

interface PericopeStudyMessageProps {
  response: ScriptureResponse;
  commentary?: string;
  commentaryState: 'not-requested' | 'loading' | 'loaded' | 'error';
  onRequestCommentary: (readerThoughts?: string) => void;
  /** Saved thoughts from the open study — seeds the accordion's initial value. */
  initialThoughts?: string;
  progressSteps?: ProgressStep[];
  inferenceProgress?: InferenceProgress;
  inferenceMeta?: InferenceMeta;
}

export function PericopeStudyMessage({
  response,
  commentary,
  commentaryState,
  onRequestCommentary,
  initialThoughts,
  progressSteps,
  inferenceProgress,
  inferenceMeta,
}: PericopeStudyMessageProps) {
  const pericope = response.pericope;
  const anchorRef = response.verses[0]?.reference;
  // Experimental seed notes — captured locally until Generate is pressed,
  // then handed to the request (which parks them on the source message).
  // Pre-filled from the open study's saved thoughts when launched from the
  // editor cue.
  const [thoughts, setThoughts] = useState(initialThoughts ?? '');

  return (
    <div className="pericope-study-message">
      {pericope && <PericopeSection pericope={pericope} />}

      {commentary && (
        <CommentarySection
          commentary={commentary}
          anchorContext={deriveAnchorContext(
            pericope,
            anchorRef,
            response.verses.map((v) => ({ verse: v.verse, text: v.text })),
          )}
        />
      )}

      {/* Its own panel, below In Context: open before generation (an
          invitation, not a hidden field), folded — never hidden — once the
          commentary lands, so the reader still sees what seeded it. */}
      <ThoughtsAccordion
        value={thoughts}
        onChange={setThoughts}
        open={commentaryState === 'not-requested'}
      />

      <CommentaryButton
        state={commentaryState}
        onClick={() => onRequestCommentary(thoughts.trim() || undefined)}
        progressSteps={progressSteps}
        inferenceProgress={inferenceProgress}
        inferenceMeta={inferenceMeta}
      />
    </div>
  );
}
